import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles } from '@coreai/fluentui-react';

export type ClusterStubNodeData = {
  /** Side of the anchor that faces (and connects back to) the container. */
  handlePosition: Position;
};

export type ClusterStubNodeType = Node<ClusterStubNodeData, 'clusterStub'>;

const useStyles = makeStyles({
  // A zero-footprint, non-interactive anchor. It only exists so the collapsed
  // cluster edge has a stable target to terminate on a fixed distance outward
  // from the container's fan-out handle. The visible collapse chip rides the
  // edge itself (see `ClusterEdge`), so this node renders nothing but a handle.
  anchor: {
    width: '1px',
    height: '1px',
    pointerEvents: 'none',
  },
  handle: {
    opacity: 0,
    pointerEvents: 'none',
  },
});

/**
 * Invisible anchor for a collapsed container's cluster edge.
 *
 * When a container (agent / if / switch / foreach) is collapsed, its children +
 * dotted edges hide and a single dotted `cluster` edge is drawn from the
 * container's fan-out handle to this anchor. The visible count badge rides the
 * edge at the fan-out handle (see `ClusterEdge`), so this node is intentionally
 * empty: a 1x1, non-draggable, non-selectable point that only provides a stable
 * edge target.
 */
export const ClusterStubNode = memo(({ data }: NodeProps<ClusterStubNodeType>) => {
  const styles = useStyles();
  return (
    <div className={styles.anchor} aria-hidden="true">
      <Handle
        type="target"
        position={data.handlePosition ?? Position.Bottom}
        id="input"
        isConnectable={false}
        className={styles.handle}
      />
    </div>
  );
});

ClusterStubNode.displayName = 'ClusterStubNode';

export default ClusterStubNode;
